import type { MarkdownDocument } from '@markbird/shared';

/**
 * Wiki-style links: `[[Target]]`, `[[Target|shown text]]`, `[[Target#Heading]]`,
 * `[[Target^block-id]]`, `[[#Heading]]` (same document) and the embed form
 * `![[Target]]`.
 *
 * They connect documents by *title*, not by id, which is what makes a note
 * portable: the same `[[…]]` text means the same thing in the app, in a `.md`
 * file on disk, and in another editor. Resolution therefore happens at read
 * time against the current library rather than being baked into the document.
 */

/** One `[[…]]` occurrence, parsed. */
export interface WikiRef {
  /** Normalized target title, lowercased. Empty for a same-document `[[#H]]`. */
  target: string;
  /** Target exactly as written, for display and for creating a missing note. */
  rawTarget: string;
  /** Heading fragment after `#`, or null. */
  heading: string | null;
  /** Block id after `^`, or null. */
  block: string | null;
  /** Display text after `|`, or null. */
  alias: string | null;
  /** True for the `![[…]]` transclusion form. */
  embed: boolean;
  /** Offsets into the *masked* source (same length as the original). */
  start: number;
  end: number;
}

/** Matches a whole `[[…]]` / `![[…]]`, capturing the inner text. */
const REF_RE = /(!?)\[\[([^\]\n]+?)\]\]/g;

/** Kept for the preview's DOM pass, which tests text nodes before walking them. */
export const WIKILINK_RE = /\[\[([^\]|\n]+?)(?:\|([^\]\n]+?))?\]\]/g;

/** Normalize a title for case-insensitive matching. */
export function key(title: string): string {
  return title.trim().toLowerCase();
}

/**
 * Blank out code so links inside it are not indexed.
 *
 * Replacing with spaces rather than deleting keeps every offset valid, so the
 * caller can still map a match back to a position in the original text. Fenced
 * blocks are handled before inline spans because a backtick inside a fence is
 * not an inline delimiter.
 */
export function maskCode(content: string): string {
  const blank = (s: string): string => s.replace(/[^\n]/g, ' ');
  return content
    .replace(/^([ \t]*)(```+|~~~+)[^\n]*\n[\s\S]*?(?:^[ \t]*\2[^\n]*$|$)/gm, blank)
    .replace(/`+[^`\n]*`+/g, blank);
}

/** Every `[[…]]` in a document, in source order, ignoring those inside code. */
export function parseWikiRefs(content: string): WikiRef[] {
  const masked = maskCode(content);
  const refs: WikiRef[] = [];
  const re = new RegExp(REF_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(masked))) {
    const inner = m[2] ?? '';
    const pipe = inner.indexOf('|');
    const alias = pipe >= 0 ? inner.slice(pipe + 1).trim() : null;
    let path = (pipe >= 0 ? inner.slice(0, pipe) : inner).trim();

    let block: string | null = null;
    let heading: string | null = null;
    // `^` binds tighter than `#`: a block id may not contain either character.
    const caret = path.indexOf('^');
    if (caret >= 0) {
      block = path.slice(caret + 1).trim() || null;
      path = path.slice(0, caret).trim();
    }
    const hash = path.indexOf('#');
    if (hash >= 0) {
      heading = path.slice(hash + 1).trim() || null;
      path = path.slice(0, hash).trim();
    }

    if (!path && !heading && !block) continue;
    refs.push({
      target: key(path),
      rawTarget: path,
      heading,
      block,
      alias,
      embed: m[1] === '!',
      start: m.index,
      end: m.index + m[0].length,
    });
  }
  return refs;
}

/** Titles referenced by `[[...]]` in a document (lowercased, de-duplicated). */
export function wikiTargets(content: string): string[] {
  const out = new Set<string>();
  for (const ref of parseWikiRefs(content)) {
    if (ref.target) out.add(ref.target);
  }
  return [...out];
}

/** Every name a document answers to: its title plus its aliases, normalized. */
export function documentKeys(doc: Pick<MarkdownDocument, 'title' | 'aliases'>): string[] {
  const keys = [key(doc.title || ''), ...(doc.aliases ?? []).map(key)];
  return [...new Set(keys.filter(Boolean))];
}

/** Resolve a referenced title to a document id, matching titles then aliases. */
export function resolveTitleToId(docs: MarkdownDocument[], title: string): string | null {
  const k = key(title);
  if (!k) return null;
  const byTitle = docs.find((d) => key(d.title || '') === k);
  if (byTitle) return byTitle.id;
  return docs.find((d) => (d.aliases ?? []).some((a) => key(a) === k))?.id ?? null;
}

/**
 * Documents that link to `title` via `[[title]]`.
 *
 * This scans the full text of every document, so it is only appropriate for
 * one-off checks and tests. Anything user-facing must query the link index
 * (`lib/linkIndex`), which keeps the same answer without the linear scan.
 */
export function findBacklinks(
  docs: MarkdownDocument[],
  selfId: string,
  title: string,
): MarkdownDocument[] {
  const k = key(title);
  if (!k) return [];
  return docs.filter((d) => d.id !== selfId && wikiTargets(d.content).includes(k));
}

/** Cheap gate so the DOM walker only descends into text that could match. */
const HAS_WIKILINK = /\[\[[^\]\n]+?\]\]/;

/**
 * Replace `[[…]]` text in a rendered preview with clickable anchors
 * (`.mb-wikilink[data-title]`), skipping code and existing links.
 *
 * This runs on the preview DOM rather than in the shared render pipeline
 * because resolution depends on the local library, which exports and shares do
 * not have — there, the plain `[[Title]]` text is left standing, which reads
 * fine as a convention. The caller wires a delegated click handler.
 */
export function resolveWikiLinksInDom(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p || p.closest('a, code, pre')) return NodeFilter.FILTER_REJECT;
      return HAS_WIKILINK.test(node.nodeValue || '')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) targets.push(n as Text);

  for (const text of targets) {
    const value = text.nodeValue || '';
    const refs = parseWikiRefs(value);
    if (refs.length === 0) continue;

    const frag = document.createDocumentFragment();
    let last = 0;
    for (const ref of refs) {
      if (ref.start > last) frag.appendChild(document.createTextNode(value.slice(last, ref.start)));
      const a = document.createElement('a');
      a.className = 'mb-wikilink';
      a.dataset.title = ref.rawTarget;
      if (ref.heading) a.dataset.heading = ref.heading;
      if (ref.block) a.dataset.block = ref.block;
      a.textContent = ref.alias ?? displayText(ref);
      a.setAttribute('role', 'link');
      a.tabIndex = 0;
      frag.appendChild(a);
      last = ref.end;
    }
    if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
    text.parentNode?.replaceChild(frag, text);
  }
}

/** How a ref reads when it carries no explicit alias. */
export function displayText(ref: WikiRef): string {
  if (!ref.rawTarget) return ref.heading ?? ref.block ?? '';
  if (ref.heading) return `${ref.rawTarget} › ${ref.heading}`;
  return ref.rawTarget;
}
